import type AccessIssue from 'owa-attachment-policy-access-issue-checker/lib/schema/AccessIssue';
import getConditionalAccessIssue from 'owa-attachment-policy-access-issue-checker/lib/utils/getConditionalAccessIssue';
import type { MailboxInfo } from 'owa-client-types';
import getModuleContextMailboxInfo from 'owa-module-context-mailboxinfo/lib/selectors/getModuleContextMailboxInfo';
import { isVanityDomain } from 'owa-tokenprovider';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * Existing isFeatureEnabled usage — migrate to isGlobalFeatureEnabled or isAccountFeatureEnabled
 *	> 'isFeatureEnabled' import from 'owa-feature-flags' is restricted. Use isGlobalFeatureEnabled or isAccountFeatureEnabled from owa-feature-flags instead */
import { isFeatureEnabled, isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isCapabilityEnabled } from 'owa-capabilities';
import { m365AcquisitionsCapability } from 'owa-capabilities-definitions/lib/m365AcquisitionsCapability';
import { logCoreGreyError, logUsage } from 'owa-analytics';
import { trace } from 'owa-trace';
import type { TraceErrorObject } from 'owa-analytics-types';
import { getApplicationSettings } from 'owa-application-settings';
import { isAccountExplicitLogon } from 'owa-account-shared-mailbox-utils';

export function isSupportedAppServiceEnvironment(mailboxInfo?: MailboxInfo): boolean {
    // Check if the M365Acquisitions capability is enabled
    if (!isCapabilityEnabled(m365AcquisitionsCapability, mailboxInfo)) {
        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param, owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         * undefined
         *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo
         (https://aka.ms/OWALintWiki)
                 * Baseline. Dev touching this file must update to pass the active MailboxInfo; Docs: https://aka.ms/owa-analytics-mailboxinfo
                 * undefined
                 *	> MailboxInfo must be passed into the function to identify the account.
                 (https://aka.ms/OWALintWiki)
                         * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
                         *	> Invalid Outlook telemetry EventName "MOS-AppServiceNotSupported": must contain only letters, numbers, or underscores. */
        logUsage('MOS-AppServiceNotSupported', undefined, {
            AddInsCapabilitySupported: false,
        });

        trace.warn(`MOS-AppServiceNotSupported- Disabled Capability`, 'addins');

        return false;
    }

    const mboxInfo = mailboxInfo ?? getModuleContextMailboxInfo();
    const isExplicitLogon = isAccountExplicitLogon(mboxInfo);
    const combinedAccessIssue = getConditionalAccessIssue(mboxInfo).shift() ?? 2;
    const isVanityDomainValue = isVanityDomain();
    if (combinedAccessIssue !== 2) {
        const mosAppServiceNotSupportedError: TraceErrorObject = new Error(
            'MosAppServiceNotSupported'
        );

        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logCoreGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
        logCoreGreyError('MosAppServiceNotSupported', undefined, mosAppServiceNotSupportedError, {
            combinedAccessIssue,
            isExplicitLogon,
            isVanityDomainValue,
        });
        trace.warn(
            `MOS-AppServiceNotSupported-
            combinedAccessIssue-${combinedAccessIssue},
            isExplicitLogon-${isExplicitLogon},
            isVanityDomain-${isVanityDomainValue}`,
            'addins'
        );
    }

    const mosAppinVanityDomain =
        !isVanityDomainValue || getApplicationSettings('OwaVanityDomains').enabled;

    const explicitLogonSupport = isGlobalFeatureEnabled('addin-sharedMailboxNewtab')
        ? true
        : !isExplicitLogon;

    return (
        combinedAccessIssue === 2 &&
        explicitLogonSupport &&
        mosAppinVanityDomain &&
        /* eslint-disable-next-line owa-custom-rules/require-undefined-parameter -- (https://aka.ms/OWALintWiki)
         * Flight checks that supply MailboxInfo should be defined as AccountFeatureName value and should be checked using isAccountFeatureEnabled to ensure consistent checking.
         *	> The parameter mailboxInfo must be undefined. Feature flight: 'mos-mos3AppServiceSupportedEnv' */
        isFeatureEnabled('mos-mos3AppServiceSupportedEnv', mailboxInfo)
    );
}
